import { useContext, useReducer } from 'react';
import produce from 'immer';
import { WorkspaceContext } from '../views/workspace/workspaceContext';

// idk why I can't import this?? possibly a circular dependency or build config issue
export function getPropertiesOfType(DIL, type) {
	if (!DIL) return [];

	const { propertyList: properties } = DIL;
	// @ts-ignore
	return Object.values(properties).filter((property) => property.type === type);
}

export function useWorkspaceState() {
	const [state, dispatch] = useReducer(reducer, initialState);
	return [state, dispatch];
}

/**
 * Creates a default card appropriate for the given card index.
 * @param cardIndex
 * @param widgets
 * @param selectedDefinitionName
 * @returns {{endpointQuery: null, type: *}}
 */
const createDefaultCard = (cardIndex, widgets, selectedDefinitionName) => {
	let widgetIndex = cardIndex % widgets.length;
	let widget = widgets[widgetIndex];
	return createDefaultCardForType(widget, widgets, selectedDefinitionName);
};

/**
 * Creates a default card for the given widget
 * @param widget
 * @param widgets
 * @param selectedDefinitionName
 * @returns {{endpointQuery: null, type: *}}
 */
const createDefaultCardForType = (widget, widgets, selectedDefinitionName) => {
	let card = {
		type: widget.key,
		endpointQuery: null,
		widgetConfiguration: null,
	};

	let widgetConfigurations = widget['configurations'][selectedDefinitionName];
	if (widgetConfigurations) {
		let widgetConfiguration = widgetConfigurations.default;
		if (widgetConfiguration) {
			card.endpointQuery = widgetConfiguration.endpointQuery;
			card.widgetConfiguration = widgetConfiguration.widgetConfiguration;
		}
	}

	return card;
};

const reducer = produce((draft, action) => {
	// eslint-disable-next-line no-console
	// uncomment above for useful debugging output.
	switch (action.type) {
		case WorkspaceActions.CREATE_NEW_EXPLORER: {
			// This can be configurable if we need it
			let explorer = {
				layoutStyle: {
					key: 'ONE',
					cardCount: 1,
				},
			};

			// determine how many cards we should have based on layoutStyle and create a new card for each
			explorer.cards = [];
			for (let i = 0; i < explorer.layoutStyle.cardCount; i++) {
				explorer.cards[i] = createDefaultCard(i, draft.transient.widgets, draft.explorer.selectedDefinitionName);
			}
			draft.explorer.layout = explorer;
			break;
		}
		case WorkspaceActions.UPDATE_SELECTED_DEFINITION: {
			const { newDefinition } = action.payload;
			draft.explorer.selectedDefinitionName = newDefinition && newDefinition.name;
			draft.explorer.metaData = undefined;

			// create a new card because the properties will be different
			if (draft && draft.explorer.layout && draft.explorer.layout.cards) {
				draft.explorer.layout.cards = draft.explorer.layout.cards.map((card) => {
					const widget = draft.transient.widgets.find((c) => c.key === card.type) ?? draft.transient.widgets[0];

					return createDefaultCardForType(widget, draft.transient.widgets, newDefinition.name);
				});
			}

			// Reset the filters
			draft.explorer.filters = {};
			draft.transient.queryStrings = {};
			draft.transient.filters = {};

			// Reset results
			draft.transient.rootResult = null;
			draft.transient.activeResult = null;

			break;
		}
		case WorkspaceActions.UPDATE_RESULT: {
			const { result, rootResult } = action.payload;
			draft.transient.activeResult = result;
			draft.transient.rootResult = rootResult;
			break;
		}
		case WorkspaceActions.UPDATE_EXPLORER_LAYOUT: {
			let { payload } = action.payload;
			draft.explorer.layout.layoutStyle = payload;

			// check card count, if too many then remove, if too few create
			let cards = draft.explorer.layout.cards;
			if (payload.cardCount < cards.length) {
				cards.splice(payload.cardCount);
			} else if (payload.cardCount > cards.length) {
				for (let i = cards.length; i < payload.cardCount; i++) {
					cards[i] = createDefaultCard(i, draft.transient.widgets, draft.explorer.selectedDefinitionName);
				}
			}
			break;
		}
		case WorkspaceActions.MAXIMIZE_WIDGET: {
			// TODO: Not sure if this is extensible
			draft.explorer.layout.layoutStyle = { cardCount: 1, key: 'ONE' };

			// check card count, if too many then remove, if too few create
			let cards = draft.explorer.layout.cards;
			if (cards.length !== 1) {
				cards.splice(0, cards.length, cards[action.payload]);
			}
			break;
		}
		case WorkspaceActions.UPDATE_EXPLORER_CARD: {
			const { cardIndex, newCard } = action.payload;
			draft.explorer.layout.cards[cardIndex] = newCard;
			break;
		}
		case WorkspaceActions.UPDATE_WIDGET_CONFIGURATION: {
			const { cardIndex, configuration } = action.payload;
			draft.explorer.layout.cards[cardIndex].widgetConfiguration = configuration;
			break;
		}
		case WorkspaceActions.UPDATE_EXPLORER_CARD_WIDGET: {
			const { cardIndex, payload } = action.payload;
			const widget = draft.transient.widgets.find((c) => c.key === payload);
			draft.explorer.layout.cards[cardIndex] = createDefaultCardForType(
				widget,
				draft.transient.widgets,
				draft.explorer.selectedDefinitionName
			);
			break;
		}
		case WorkspaceActions.UPDATE_EXPLORER_CARD_ACTIONS: {
			const { cardIndex, actions } = action.payload;
			draft.explorer.layout.cards[cardIndex].actions = actions;
			break;
		}
		case WorkspaceActions.PATCH_EXPLORER_CARD_ENDPOINT_PARAMETERS: {
			const { cardIndex, parameters } = action.payload;
			draft.explorer.layout.cards[cardIndex].endpointQuery.options.parameters = {
				...draft.explorer.layout.cards[cardIndex].endpointQuery.options.parameters,
				...parameters,
			};
			break;
		}
		case WorkspaceActions.UPDATE_EXPLORER_CARD_ENDPOINT_OPTIONS: {
			const { cardIndex, payload } = action.payload;
			draft.explorer.layout.cards[cardIndex].endpointQuery = payload;
			break;
		}
		case WorkspaceActions.SET_CURRENT_EXPLORER: {
			draft.explorer = action.payload;
			break;
		}
		case WorkspaceActions.UPDATE_FILTER: {
			const { filterKey, filter } = action.payload;
			draft.explorer.filters[filterKey] = filter;
			break;
		}
		// React batches non-async programmatic state changes which can lead to some
		// changes overriding each other. Use this to make updates based on previous
		// state (not out of date closures).
		case WorkspaceActions.UPDATE_FILTER_CALLBACK: {
			const { filterKey, callback } = action.payload;
			const currentFilters = draft.explorer.filters[filterKey];
			if (callback) {
				draft.explorer.filters[filterKey] = callback(currentFilters);
			}
			break;
		}
		case WorkspaceActions.UPDATE_TRANSIENT_FILTER: {
			const { filterKey, filter } = action.payload;
			draft.transient.filters[filterKey] = filter;
			break;
		}
		case WorkspaceActions.UPDATE_QUERY_STRING: {
			const { filterKey, queryString } = action.payload;
			draft.transient.queryStrings[filterKey] = queryString;
			break;
		}
		case WorkspaceActions.FORCE_REFRESH: {
			draft.transient.refreshTime = Date.now();
			break;
		}
		case WorkspaceActions.LOAD_SAVED_VIEW: {
			const { key, payload } = action.payload;
			draft.transient.activeResult = draft.transient.rootResult;
			draft.transient.queryStrings = {};
			draft[key] = payload;
			break;
		}
		case WorkspaceActions.LOAD_EMPTY_WIDGET: {
			draft.explorer.layout.cards = [];
			break;
		}
		case WorkspaceActions.UPDATE_VIEW_PROPERTIES: {
			const { key, payload } = action.payload;
			draft.explorer.views[key] = payload;
			break;
		}
		case WorkspaceActions.UPDATE_WIDGETS: {
			draft.transient.widgets = action.payload;
			if (draft && draft.explorer.layout && draft.explorer.layout.cards) {
				// needed for saved views to prevent reloading the explorer
				if (draft.explorer.layout.cards?.[0].widgetConfiguration) return;
				draft.explorer.layout.cards = draft.explorer.layout.cards.map((card) => {
					const widget = draft.transient.widgets.find((c) => c.key === card.type);
					if (widget)
						return createDefaultCardForType(widget, draft.transient.widgets, draft.explorer.selectedDefinitionName);
					//if widget wasn't found it is not supported in the selectedDefinitionName so just replace it with the first available widget
					//(We can't just remove it bc that would require layout updates, and that could get more involved than we need to. This is a simple fix)
					return createDefaultCardForType(
						draft.transient.widgets[0],
						draft.transient.widgets,
						draft.explorer.selectedDefinitionName
					);
				});
			}
			break;
		}
		case WorkspaceActions.UPDATE_META_DATA: {
			draft.explorer.metaData = action.payload;
			break;
		}
		case WorkspaceActions.UPDATE_SELECTION: {
			draft.transient.selection = action.payload;
			break;
		}
		default:
			break;
	}
});

let initialState = {
	explorer: {
		layout: null,
		filters: {},
		selectedDefinitionName: null,
		views: {},
	},
	transient: {
		rootResult: null,
		activeResult: null,
		refreshTime: Date.now(),
		filters: {},
		queryStrings: {},
		widgets: [],
		selection: null,
	},
};

export const WorkspaceActions = {
	CREATE_NEW_EXPLORER: 'CREATE_NEW_EXPLORER',
	UPDATE_SELECTED_DEFINITION: 'UPDATE_SELECTED_DEFINITION',
	UPDATE_RESULT: 'UPDATE_RESULT',
	SET_CURRENT_EXPLORER: 'SET_CURRENT_EXPLORER',
	FORCE_REFRESH: 'FORCE_REFRESH',
	UPDATE_EXPLORER_CARD: 'UPDATE_EXPLORER_CARD',
	UPDATE_EXPLORER_CARD_WIDGET: 'UPDATE_EXPLORER_CARD_WIDGET',
	UPDATE_EXPLORER_LAYOUT: 'UPDATE_EXPLORER_LAYOUT',
	MAXIMIZE_WIDGET: 'MAXIMIZE_WIDGET',
	UPDATE_EXPLORER_CARD_ENDPOINT_OPTIONS: 'UPDATE_EXPLORER_CARD_ENDPOINT_OPTIONS',
	UPDATE_EXPLORER_CARD_ACTIONS: 'UPDATE_EXPLORER_CARD_ACTIONS',
	PATCH_EXPLORER_CARD_ENDPOINT_PARAMETERS: 'PATCH_EXPLORER_CARD_ENDPOINT_PARAMETERS',
	UPDATE_WIDGET_CONFIGURATION: 'UPDATE_WIDGET_CONFIGURATION',
	UPDATE_FILTER: 'UPDATE_FILTER',
	UPDATE_FILTER_CALLBACK: 'UPDATE_FILTER_CALLBACK',
	UPDATE_TRANSIENT_FILTER: 'UPDATE_TRANSIENT_FILTER',
	UPDATE_QUERY_STRING: 'UPDATE_QUERY_STRING',
	LOAD_SAVED_VIEW: 'LOAD_SAVED_VIEW',
	LOAD_EMPTY_WIDGET: 'LOAD_EMPTY_WIDGET',
	UPDATE_VIEW_PROPERTIES: 'UPDATE_VIEW_PROPERTIES',
	UPDATE_WIDGETS: 'UPDATE_WIDGETS',
	UPDATE_META_DATA: 'UPDATE_META_DATA',
	UPDATE_SELECTION: 'UPDATE_SELECTION',
};

export const WorkspaceActionCreator = {
	configuration: (cardIndex, configuration) => ({
		type: WorkspaceActions.UPDATE_WIDGET_CONFIGURATION,
		payload: {
			cardIndex,
			configuration,
		},
	}),
	createNewExplorer: () => ({
		type: WorkspaceActions.CREATE_NEW_EXPLORER,
	}),
	updateSelectedDefinition: (newDefinition, savedStates) => ({
		type: WorkspaceActions.UPDATE_SELECTED_DEFINITION,
		payload: {
			newDefinition,
			savedStates,
		},
	}),
	patchParameters: (cardIndex, parameterPatch) => ({
		type: WorkspaceActions.PATCH_EXPLORER_CARD_ENDPOINT_PARAMETERS,
		payload: {
			cardIndex,
			parameters: parameterPatch,
		},
	}),
	options: (cardIndex, payload) => ({
		type: WorkspaceActions.UPDATE_EXPLORER_CARD_ENDPOINT_OPTIONS,
		payload: {
			cardIndex,
			payload,
		},
	}),
	updateResult: (result, rootResult) => ({
		type: WorkspaceActions.UPDATE_RESULT,
		payload: {
			result,
			rootResult,
		},
	}),
	updateExplorerCard: (newCard, cardIndex) => ({
		type: WorkspaceActions.UPDATE_EXPLORER_CARD,
		payload: {
			cardIndex,
			newCard,
		},
	}),
	updateExplorerLayout: (payload, savedStates) => ({
		type: WorkspaceActions.UPDATE_EXPLORER_LAYOUT,
		payload: {
			payload,
			savedStates,
		},
	}),
	updateExplorerCardWidget: (cardIndex, payload, savedStates) => ({
		type: WorkspaceActions.UPDATE_EXPLORER_CARD_WIDGET,
		payload: {
			cardIndex,
			payload,
			savedStates,
		},
	}),
	updateExplorerCardActions: (cardIndex, actions) => ({
		type: WorkspaceActions.UPDATE_EXPLORER_CARD_ACTIONS,
		payload: {
			cardIndex,
			actions,
		},
	}),
	setCurrentExplorer: (explorerKey, selectedDefinitionName) => ({
		type: WorkspaceActions.SET_CURRENT_EXPLORER,
		payload: {
			explorerKey,
			selectedDefinitionName,
		},
	}),
	maximizeWidget: (cardIndex) => ({
		type: WorkspaceActions.MAXIMIZE_WIDGET,
		payload: cardIndex,
	}),
	updateFilter: (filterKey, filter) => ({
		type: WorkspaceActions.UPDATE_FILTER,
		payload: {
			filterKey,
			filter,
		},
	}),
	updateFilterCallback: (filterKey, callback) => ({
		type: WorkspaceActions.UPDATE_FILTER_CALLBACK,
		payload: {
			filterKey,
			callback,
		},
	}),
	updateTransientFilter: (filterKey, filter) => ({
		type: WorkspaceActions.UPDATE_TRANSIENT_FILTER,
		payload: {
			filterKey,
			filter,
		},
	}),
	updateQueryString: (filterKey, queryString) => ({
		type: WorkspaceActions.UPDATE_QUERY_STRING,
		payload: {
			filterKey,
			queryString,
		},
	}),
	forceRefresh: () => ({
		type: WorkspaceActions.FORCE_REFRESH,
	}),
	loadSavedView: (key, payload) => ({
		type: WorkspaceActions.LOAD_SAVED_VIEW,
		payload: {
			key,
			payload,
		},
	}),
	loadEmptyWidget: () => ({
		type: WorkspaceActions.LOAD_EMPTY_WIDGET,
		payload: {},
	}),
	updateViewProperties: (key, payload) => ({
		type: WorkspaceActions.UPDATE_VIEW_PROPERTIES,
		payload: {
			key,
			payload,
		},
	}),
	updateWidgets: (widgets) => ({
		type: WorkspaceActions.UPDATE_WIDGETS,
		payload: widgets,
	}),
	updateMetaData: (metaData) => ({
		type: WorkspaceActions.UPDATE_META_DATA,
		payload: metaData,
	}),
	updateSelection: (selection) => ({
		type: WorkspaceActions.UPDATE_SELECTION,
		payload: selection,
	}),
};

export function useIsWorkspaceAvailable() {
	const context = useContext(WorkspaceContext);
	return !!context;
}
